import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './NavWalkthrough.css';

const STEPS = [
  {
    anchor: '[data-nav-tour="products"]',
    titleKey: 'NavTour.Step1Title',
    titleDefault: 'Your products are now on the left.',
    copyKey: 'NavTour.Step1Copy',
    copyDefault: 'Locked items can still be opened to Talk to Expert.',
  },
  {
    anchor: '[data-nav-tour="customize"]',
    fallback: '[data-nav-tour="products"]',
    titleKey: 'NavTour.Step3Title',
    titleDefault: 'Switch or customize anytime.',
    copyKey: 'NavTour.Step3Copy',
    copyDefault: 'Open Customize navigation in the left menu.',
  },
];

const findAnchor = (step) => {
  const current = STEPS[step] || STEPS[0];
  return (
    document.querySelector(current.anchor) ||
    (current.fallback && document.querySelector(current.fallback)) ||
    document.querySelector('[data-nav-tour="products"]')
  );
};

const NavWalkthrough = ({ open, onFinish, t }) => {
  const [index, setIndex] = useState(0);
  const [coords, setCoords] = useState({ top: 120, left: 320 });

  useEffect(() => {
    if (open) {
      setIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const update = () => {
      const node = findAnchor(index);
      if (!node) {
        return;
      }
      const rect = node.getBoundingClientRect();
      setCoords({
        top: Math.max(72, Math.min(rect.top, window.innerHeight - 180)),
        left: rect.right + 12,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const timer = window.setTimeout(update, 50);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.clearTimeout(timer);
    };
  }, [open, index]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const current = STEPS[index] || STEPS[0];
  const last = index >= STEPS.length - 1;

  const stopEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation();
    }
  };

  const handleNext = (event) => {
    stopEvent(event);
    setIndex((currentIndex) =>
      Math.min(currentIndex + 1, STEPS.length - 1)
    );
  };

  const handleFinish = (event) => {
    stopEvent(event);
    if (onFinish) {
      onFinish();
    }
  };

  return ReactDOM.createPortal(
    <div
      className="nav-walkthrough-card"
      data-tour="nav-walkthrough"
      style={{ top: coords.top, left: coords.left }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="nav-walkthrough-step">
        {index + 1} of {STEPS.length}
      </div>
      <h3 key={'title-' + index}>
        {t(current.titleKey, { defaultValue: current.titleDefault })}
      </h3>
      <p key={'copy-' + index}>
        {t(current.copyKey, { defaultValue: current.copyDefault })}
      </p>
      <div className="nav-walkthrough-actions">
        {last ? (
          <button
            type="button"
            className="nav-walkthrough-primary"
            onClick={handleFinish}
          >
            {t('NavTour.Finish', { defaultValue: 'Finish' })}
          </button>
        ) : (
          <button
            type="button"
            className="nav-walkthrough-primary"
            onClick={handleNext}
          >
            {t('NavTour.Next', { defaultValue: 'Next' })}
          </button>
        )}
        <button
          type="button"
          className="nav-walkthrough-skip"
          onClick={handleFinish}
        >
          {t('NavTour.Skip', { defaultValue: 'Skip' })}
        </button>
      </div>
    </div>,
    document.body
  );
};

NavWalkthrough.propTypes = {
  open: PropTypes.bool,
  onFinish: PropTypes.func,
  t: PropTypes.func.isRequired,
};

NavWalkthrough.defaultProps = {
  open: false,
};

export default NavWalkthrough;



// WEBPACK FOOTER //
// ./src/components/leftnav/NavWalkthrough.js